import { environmentTypeOf } from '@/data/environmentTypes'
import { PRODUCTION_LICENSE_KEY_HEADING, licenseFieldsFor, type LicenseField } from '@/data/licenses'
import { useInfrastructureWizard } from '@/stores/infrastructureWizard'
import page from '@/pages/NewDeploymentPage.module.css'
import { asksLicense } from '@/utils/infraValidation'

/**
 * The license section, shown according to the type of the environment chosen in the Infrastructure flow (production, staging or
 * demo; data/environmentTypes.ts): it is not there until an environment is chosen, and it follows the choice. Production asks for the
 * Site ID and a License key in two parts (Production key and Sandbox key); staging and demo ask for a License key and a Site ID. Every
 * field is required. Asked of the product lines (Traverse, Process Pro) only.
 */
export function LicenseFields() {
  const product = useInfrastructureWizard((s) => s.product)
  const environment = useInfrastructureWizard((s) => s.environment)
  const license = useInfrastructureWizard((s) => s.license)
  const patch = useInfrastructureWizard((s) => s.patchLicense)
  const type = environmentTypeOf(environment)
  if (!asksLicense(product) || !type) return null
  const fields = licenseFieldsFor(type)
  const input = (field: LicenseField) => (
    <div key={field.key} className={page.formGroup}>
      <label htmlFor={`license-${field.key}`} className={page.label}>
        {field.label} <span className={page.required}>*</span>
      </label>
      <input id={`license-${field.key}`} className={page.input} value={license[field.key]} onChange={(event) => patch({ [field.key]: event.target.value })} autoComplete="off" spellCheck={false} aria-required="true" />
    </div>
  )

  const production = type === 'production'
  const keyParts = fields.filter((field) => field.keyPart)
  return (
    <section aria-label="License" data-testid="license-fields" style={{ marginTop: 16 }}>
      <div className={page.formGrid}>
        {fields.filter((field) => !field.keyPart).map(input)}
        {production ? (
          <fieldset style={{ border: 'none', padding: 0, margin: 0, gridColumn: '1 / -1' }}>
            <legend className={page.label}>{PRODUCTION_LICENSE_KEY_HEADING}</legend>
            <div className={page.formGrid}>{keyParts.map(input)}</div>
          </fieldset>
        ) : null}
      </div>
      <div className={page.fieldHint}>
        {production ? 'Production environment: Site ID, and the Production and Sandbox keys.' : `${type === 'demo' ? 'Demo' : 'Staging'} environment: License key and Site ID.`} The license is not a secret and is not used
        to build infrastructure: it is kept with the request for the application deployment.
      </div>
    </section>
  )
}
